/**
 * Core traversal: semantics and params in parallel, emitting units in document
 * order. Extract/merge/progress share it, so keys and paths cannot disagree.
 */

import {
  ASSET_EMBED_LIBRARIES,
  EXCLUDED_LIBRARIES,
  FEEDBACK_RANGES_KEY,
  FEEDBACK_RANGE_FIELD,
  assetTypeFromOptions,
  isConditionalField,
  isFeedbackRangesGroup,
  isExternalUrlMediaValue,
  isFlattenedGroup,
  isGroupField,
  isLibraryField,
  isListField,
  isRecord,
  leafFieldType,
  parseLibraryValue,
  pathToString,
  stableKeyOf,
  toResolverFn,
} from './internal';
import {
  H5PParams,
  H5PSemantics,
  H5PSemanticsField,
  SemanticsResolverFn,
  TranslatableField,
  TranslatableFieldType,
  TranslatableItemInfo,
  TranslationValue,
  TRANSLATABLE_AS_URL,
  TRANSLATABLE_FIELD_TYPES,
  WalkerOptions,
} from './types';

/** An item skipped by the Drag & Drop hard exclusion (reported as 0/0). */
export interface ExcludedItem {
  readonly itemKey: string;
  readonly keyChain: readonly string[];
  readonly path: string;
  readonly library: string;
}

export interface WalkResult {
  readonly fields: TranslatableField[];
  readonly excludedItems: ExcludedItem[];
}

const OWNER_KINDS = {
  MODULE: 'module',
  KEYED: 'keyed',
  UNKEYED: 'unkeyed',
} as const;

type OwnerKind = (typeof OWNER_KINDS)[keyof typeof OWNER_KINDS];

interface Scope {
  readonly segments: ReadonlyArray<string | number>;
  /** Params path relative to the owning item (or the module root). */
  readonly relPath: readonly string[];
  readonly ownerKind: OwnerKind;
  readonly ownerKey: string | null;
  readonly ownerLabel: string;
  readonly ownerIndex?: number;
  readonly ownerPath: string;
  /** Uber name of the enclosing embedded library, if any. */
  readonly libraryUber?: string;
  readonly keyChain: readonly string[];
  /** Label of the nearest enclosing group (module-level item label). */
  readonly groupLabel: string;
}

interface WalkContext {
  readonly resolver: SemanticsResolverFn;
  readonly fields: TranslatableField[];
  readonly excludedItems: ExcludedItem[];
}

/** Walk a content's semantics + params and collect all translatable units. */
export const walkTranslatableUnits = (
  semantics: H5PSemantics,
  params: H5PParams,
  options?: WalkerOptions,
): WalkResult => {
  const context: WalkContext = {
    resolver: toResolverFn(options?.resolveSemantics),
    fields: [],
    excludedItems: [],
  };
  const rootScope: Scope = {
    segments: [],
    relPath: [],
    ownerKind: OWNER_KINDS.MODULE,
    ownerKey: null,
    ownerLabel: '',
    ownerPath: '',
    keyChain: [],
    groupLabel: '',
  };
  walkFields(semantics, params, rootScope, context);
  return { fields: context.fields, excludedItems: context.excludedItems };
};

const walkFields = (
  fields: readonly H5PSemanticsField[],
  container: unknown,
  scope: Scope,
  context: WalkContext,
): void => {
  for (const field of fields) {
    walkField(field, container, scope, context, true);
  }
};

/**
 * Walk one semantics field. Without `addSegment` `containerOrValue` already IS
 * the value; `leafNameOverride` names units whose scope has no relPath yet.
 */
const walkField = (
  field: H5PSemanticsField,
  containerOrValue: unknown,
  scope: Scope,
  context: WalkContext,
  addSegment: boolean,
  leafNameOverride?: string,
): void => {
  if (field.common === true) {
    return;
  }
  const name = typeof field.name === 'string' ? field.name : undefined;
  if (addSegment && name === undefined) {
    return;
  }
  const value = addSegment
    ? isRecord(containerOrValue)
      ? containerOrValue[name as string]
      : undefined
    : containerOrValue;
  const segments = addSegment
    ? [...scope.segments, name as string]
    : [...scope.segments];
  const relPath = addSegment
    ? [...scope.relPath, name as string]
    : [...scope.relPath];
  const fieldScope: Scope = { ...scope, segments, relPath };

  if (isFeedbackRangesGroup(field)) {
    if (field.translatable === true) {
      emitFeedbackRanges(field, value, fieldScope, context);
    }
    return;
  }
  if (isGroupField(field)) {
    walkGroup(field, value, fieldScope, context);
    return;
  }
  if (isListField(field)) {
    walkList(field, value, fieldScope, context);
    return;
  }
  if (isLibraryField(field)) {
    walkLibraryField(field, value, fieldScope, context);
    return;
  }
  if (field.translatable === true) {
    if (
      field.translatableAs === TRANSLATABLE_AS_URL &&
      !isExternalUrlMediaValue(value)
    ) {
      // URL-only field with a local-upload source: stays universal (not a
      // translatable unit, not counted).
      return;
    }
    emitLeaf(field, value, fieldScope, context, leafNameOverride);
  }
};

const walkGroup = (
  field: H5PSemanticsField,
  value: unknown,
  scope: Scope,
  context: WalkContext,
): void => {
  let groupScope: Scope = {
    ...scope,
    groupLabel: field.label || field.name || scope.groupLabel,
  };
  if (field.isSubContent === true) {
    // Sub-content groups carry their own stable key (mirrors subContentId
    // handling of embedded libraries).
    groupScope = enterItem(groupScope, stableKeyOf(value) ?? null, {
      label: field.label || field.name || '',
      index: scope.ownerIndex,
    });
  }
  if (isFlattenedGroup(field)) {
    walkField(
      field.fields?.[0] as H5PSemanticsField,
      value,
      groupScope,
      context,
      false,
    );
    return;
  }
  walkFields(field.fields ?? [], value, groupScope, context);
};

const walkList = (
  field: H5PSemanticsField,
  value: unknown,
  scope: Scope,
  context: WalkContext,
): void => {
  const itemDef = field.field;
  if (!itemDef) {
    return;
  }
  // Some custom list widgets store items nested under the list's OWN name
  // (`hotspot: { hotspot: [...] }`) — unwrap with the extra path segment.
  let items: unknown[] = [];
  let listSegments = scope.segments;
  if (Array.isArray(value)) {
    items = value;
  } else if (
    isRecord(value) &&
    typeof field.name === 'string' &&
    Array.isArray(value[field.name])
  ) {
    items = value[field.name] as unknown[];
    listSegments = [...scope.segments, field.name];
  }
  items.forEach((item, index) => {
    const itemSegments = [...listSegments, index];
    const itemScope: Scope = { ...scope, segments: itemSegments };

    // Legacy bare-string items of a `stringItemField` list (SortParagraphs
    // pre-restructure): readable but unkeyed — merge falls back to source.
    if (typeof item === 'string' && typeof field.stringItemField === 'string') {
      emitLegacyStringItem(field, itemDef, item, index, itemScope, context);
      return;
    }

    if (isGroupField(itemDef) && !isFeedbackRangesGroup(itemDef)) {
      const entered = enterItem(itemScope, stableKeyOf(item) ?? null, {
        label: itemDef.label || field.entity || itemDef.name || '',
        index,
      });
      if (isFlattenedGroup(itemDef)) {
        // The entered scope has an empty relPath — name the unit after the
        // single field so keyed items get `<id>:<fieldName>` map keys.
        const single = itemDef.fields?.[0] as H5PSemanticsField;
        walkField(
          single,
          item,
          entered,
          context,
          false,
          typeof single?.name === 'string' ? single.name : undefined,
        );
        return;
      }
      walkFields(itemDef.fields ?? [], item, entered, context);
      return;
    }

    if (isLibraryField(itemDef)) {
      // A translatable flag on a list-of-library field cannot be keyed per
      // item (no owning group) — always recurse normally.
      recurseIntoLibrary(itemDef, item, itemScope, context, index);
      return;
    }

    // List of translatable leaves (no owning object to hold an id).
    if (itemDef.translatable === true) {
      emitLeaf(
        itemDef,
        item,
        unkeyedLeafScope(field, itemDef, index, itemScope),
        context,
        typeof itemDef.name === 'string' ? itemDef.name : undefined,
      );
    }
  });
};

const walkLibraryField = (
  field: H5PSemanticsField,
  value: unknown,
  scope: Scope,
  context: WalkContext,
): void => {
  const library = parseLibraryValue(value);
  if (!library) {
    if (field.translatable === true) {
      const assetType = assetTypeFromOptions(field);
      if (assetType) {
        emitUnit(scope, context, {
          label: field.label || field.name || '',
          type: assetType,
          sourceValue: undefined,
          conditional: isConditionalField(field),
          pathSegments: [...scope.segments, 'params'],
          libraryAsset: true,
        });
      }
    }
    return;
  }
  if (EXCLUDED_LIBRARIES.includes(library.machineName)) {
    const key = library.subContentId ?? library.id;
    if (key !== undefined) {
      context.excludedItems.push({
        itemKey: key,
        keyChain: [...scope.keyChain, key],
        path: pathToString(scope.segments),
        library: library.uberName,
      });
    }
    return;
  }
  const assetType = ASSET_EMBED_LIBRARIES[library.machineName];
  if (field.translatable === true && assetType) {
    emitUnit(scope, context, {
      label: field.label || field.name || '',
      type: assetType,
      sourceValue: library.params,
      conditional: isConditionalField(field),
      pathSegments: [...scope.segments, 'params'],
      libraryAsset: true,
      embedLibrary: library.uberName,
    });
    return;
  }
  recurseIntoLibrary(field, value, scope, context, scope.ownerIndex);
};

const recurseIntoLibrary = (
  field: H5PSemanticsField,
  value: unknown,
  scope: Scope,
  context: WalkContext,
  index: number | undefined,
): void => {
  const library = parseLibraryValue(value);
  if (!library) {
    return;
  }
  if (EXCLUDED_LIBRARIES.includes(library.machineName)) {
    const key = library.subContentId ?? library.id;
    if (key !== undefined) {
      context.excludedItems.push({
        itemKey: key,
        keyChain: [...scope.keyChain, key],
        path: pathToString(scope.segments),
        library: library.uberName,
      });
    }
    return;
  }
  const subSemantics = context.resolver(library.uberName);
  if (!subSemantics) {
    // Missing sub-semantics: skip the subtree gracefully.
    return;
  }
  const embedScope: Scope = {
    ...enterItem(scope, library.subContentId ?? library.id ?? null, {
      label: field.label || field.name || '',
      index,
    }),
    libraryUber: library.uberName,
    segments: [...scope.segments, 'params'],
  };
  walkFields(subSemantics, library.params ?? {}, embedScope, context);
};

const emitFeedbackRanges = (
  field: H5PSemanticsField,
  value: unknown,
  scope: Scope,
  context: WalkContext,
): void => {
  const ranges = isRecord(value) ? value[FEEDBACK_RANGES_KEY] : undefined;
  if (!Array.isArray(ranges)) {
    return;
  }
  const conditional = isConditionalField(field);
  ranges.forEach((range, index) => {
    if (!isRecord(range)) {
      return;
    }
    const rangeSegments = [...scope.segments, FEEDBACK_RANGES_KEY, index];
    const rangeScope = enterItem(
      { ...scope, segments: rangeSegments },
      stableKeyOf(range) ?? null,
      { label: rangeLabel(field, range), index },
    );
    const feedback = range[FEEDBACK_RANGE_FIELD];
    emitUnit(
      {
        ...rangeScope,
        segments: [...rangeSegments, FEEDBACK_RANGE_FIELD],
        relPath: [FEEDBACK_RANGE_FIELD],
      },
      context,
      {
        label: field.label || field.name || FEEDBACK_RANGE_FIELD,
        type: TRANSLATABLE_FIELD_TYPES.TEXT,
        sourceValue: typeof feedback === 'string' ? feedback : undefined,
        conditional,
        libraryAsset: false,
      },
    );
  });
};

const rangeLabel = (
  field: H5PSemanticsField,
  range: Record<string, unknown>,
): string => {
  const from = range['from'];
  const to = range['to'];
  if (typeof from === 'number' && typeof to === 'number') {
    return `${from}-${to}`;
  }
  return field.label || field.name || '';
};

const emitLegacyStringItem = (
  listField: H5PSemanticsField,
  itemDef: H5PSemanticsField,
  item: string,
  index: number,
  scope: Scope,
  context: WalkContext,
): void => {
  const stringField = (itemDef.fields ?? []).find(
    child => child.name === listField.stringItemField,
  );
  if (!stringField || stringField.translatable !== true) {
    return;
  }
  const type = leafFieldType(stringField, item);
  if (!type) {
    return;
  }
  emitUnit(unkeyedLeafScope(listField, itemDef, index, scope), context, {
    label: stringField.label || stringField.name || '',
    type,
    sourceValue: item,
    conditional: isConditionalField(stringField),
    fieldNameOverride: listField.stringItemField,
    libraryAsset: false,
  });
};

/** Scope for a list item that has no object to carry a stable id. */
const unkeyedLeafScope = (
  listField: H5PSemanticsField,
  itemDef: H5PSemanticsField,
  index: number,
  scope: Scope,
): Scope => ({
  ...enterItem(scope, null, {
    label: itemDef.label || listField.entity || itemDef.name || '',
    index,
  }),
  relPath: [],
});

/** Enter a (potentially) keyed item: resets the relative path and owner. */
const enterItem = (
  scope: Scope,
  key: string | null,
  info: { label: string; index?: number },
): Scope => ({
  ...scope,
  relPath: [],
  ownerKind: key === null ? OWNER_KINDS.UNKEYED : OWNER_KINDS.KEYED,
  ownerKey: key,
  ownerLabel: info.label,
  ownerIndex: info.index,
  ownerPath: pathToString(scope.segments),
  keyChain: key === null ? scope.keyChain : [...scope.keyChain, key],
});

const emitLeaf = (
  field: H5PSemanticsField,
  value: unknown,
  scope: Scope,
  context: WalkContext,
  nameOverride?: string,
): void => {
  const type = leafFieldType(field, value);
  if (!type) {
    return;
  }
  emitUnit(scope, context, {
    label: field.label || field.name || '',
    type,
    widget: field.widget,
    sourceValue: coerceSourceValue(type, value),
    conditional: isConditionalField(field),
    fieldNameOverride: nameOverride,
    libraryAsset: false,
  });
};

const coerceSourceValue = (
  type: TranslatableFieldType,
  value: unknown,
): TranslationValue | undefined => {
  if (
    type === TRANSLATABLE_FIELD_TYPES.TEXT ||
    type === TRANSLATABLE_FIELD_TYPES.HTML
  ) {
    return typeof value === 'string' ? value : undefined;
  }
  if (Array.isArray(value)) {
    return value.filter(isRecord);
  }
  return isRecord(value) ? value : undefined;
};

interface UnitDetails {
  readonly label: string;
  readonly type: TranslatableFieldType;
  readonly widget?: string;
  readonly sourceValue: TranslationValue | undefined;
  readonly conditional: boolean;
  readonly pathSegments?: ReadonlyArray<string | number>;
  readonly libraryAsset: boolean;
  readonly embedLibrary?: string;
  readonly fieldNameOverride?: string;
}

const emitUnit = (
  scope: Scope,
  context: WalkContext,
  details: UnitDetails,
): void => {
  const pathSegments = details.pathSegments ?? scope.segments;
  let itemKey: string | null;
  let fieldName: string;
  let keyChain: readonly string[];
  let item: TranslatableItemInfo;

  if (scope.ownerKind === OWNER_KINDS.MODULE) {
    const parts =
      details.fieldNameOverride !== undefined
        ? [...scope.relPath, details.fieldNameOverride]
        : [...scope.relPath];
    fieldName = parts[parts.length - 1] ?? '';
    itemKey = parts.slice(0, -1).join('/');
    keyChain = [...scope.keyChain, itemKey];
    item = {
      itemKey,
      label: scope.groupLabel,
      library: scope.libraryUber,
      path: itemKey,
    };
  } else {
    itemKey = scope.ownerKind === OWNER_KINDS.KEYED ? scope.ownerKey : null;
    fieldName =
      details.fieldNameOverride !== undefined
        ? details.fieldNameOverride
        : scope.relPath.join('/');
    keyChain = scope.keyChain;
    item = {
      itemKey,
      label: scope.ownerLabel,
      library: scope.libraryUber,
      index: scope.ownerIndex,
      path: scope.ownerPath,
    };
  }

  context.fields.push({
    itemKey,
    fieldName,
    mapKey: itemKey === null ? null : `${itemKey}:${fieldName}`,
    path: pathToString(pathSegments),
    pathSegments,
    label: details.label,
    type: details.type,
    widget: details.widget,
    sourceValue: details.sourceValue,
    conditional: details.conditional,
    libraryAsset: details.libraryAsset,
    embedLibrary: details.embedLibrary,
    keyChain,
    item,
  });
};
